import React, { useState, useMemo } from 'react';
import './CacheStatus.css';

const CacheStatus = ({ cacheInfo }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('pair');
  const [sortOrder, setSortOrder] = useState('asc');

  const pairs = cacheInfo?.pairs;
  const cache_keys_count = cacheInfo?.cache_keys_count;
  const redis_memory = cacheInfo?.redis_memory;

  // Transform pairs data for display
  const pairsArray = useMemo(() => {
    if (!pairs) return [];
    
    return Object.entries(pairs).map(([pairKey, timeframes]) => {
      // pairKey format: "BTCUSDT\nbybit:futures"
      const [pair, exchangeInfo] = pairKey.split('\n');
      
      const timeframesList = Object.entries(timeframes).map(([tf, data]) => ({
        timeframe: tf,
        ttl: data.ttl || 0,
        exists: data.exists
      }));
      
      // Calculate average TTL
      const avgTtl = timeframesList.length > 0
        ? Math.round(timeframesList.reduce((sum, t) => sum + t.ttl, 0) / timeframesList.length)
        : 0;
      
      return {
        pair,
        exchange: exchangeInfo || 'unknown',
        timeframes: timeframesList,
        avgTtl,
        timeframeCount: timeframesList.length
      };
    });
  }, [pairs]);

  // Filter and sort pairs
  const filteredPairs = useMemo(() => {
    let result = [...pairsArray];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p => 
        p.pair.toLowerCase().includes(term) ||
        p.exchange.toLowerCase().includes(term)
      );
    }
    
    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'pair':
          comparison = a.pair.localeCompare(b.pair);
          break;
        case 'exchange':
          comparison = a.exchange.localeCompare(b.exchange);
          break;
        case 'ttl':
          comparison = a.avgTtl - b.avgTtl;
          break;
        case 'timeframes':
          comparison = a.timeframeCount - b.timeframeCount;
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [pairsArray, searchTerm, sortBy, sortOrder]);

  if (!cacheInfo) {
    return <div className="loading">Загрузка информации о кэше...</div>;
  }

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getTtlColor = (ttl) => {
    if (ttl > 60) return 'ttl-good';
    if (ttl > 30) return 'ttl-warning';
    return 'ttl-critical';
  };

  return (
    <div className="cache-status">
      {/* Summary Cards */}
      <div className="cache-summary">
        <div className="summary-card">
          <div className="summary-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">{cache_keys_count || 0}</span>
            <span className="summary-label">Ключей в кэше</span>
          </div>
        </div>
        
        <div className="summary-card">
          <div className="summary-icon memory">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="4" y="4" width="16" height="16" rx="2" ry="2"/>
              <rect x="9" y="9" width="6" height="6"/>
              <line x1="9" y1="1" x2="9" y2="4"/>
              <line x1="15" y1="1" x2="15" y2="4"/>
              <line x1="9" y1="20" x2="9" y2="23"/>
              <line x1="15" y1="20" x2="15" y2="23"/>
              <line x1="20" y1="9" x2="23" y2="9"/>
              <line x1="20" y1="14" x2="23" y2="14"/>
              <line x1="1" y1="9" x2="4" y2="9"/>
              <line x1="1" y1="14" x2="4" y2="14"/>
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">{redis_memory?.used_memory || 'N/A'}</span>
            <span className="summary-label">Память Redis</span>
          </div>
        </div>
        
        <div className="summary-card">
          <div className="summary-icon pairs">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">{pairsArray.length}</span>
            <span className="summary-label">Торговых пар</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="cache-search">
        <div className="search-input-wrapper">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Поиск по паре или бирже..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="search-clear" onClick={() => setSearchTerm('')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>
        <span className="search-results">
          {filteredPairs.length} из {pairsArray.length} пар
        </span>
      </div>

      {/* Table */}
      <div className="cache-table-wrapper">
        <table className="cache-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('pair')} className="sortable">
                Пара {getSortIcon('pair')}
              </th>
              <th onClick={() => handleSort('exchange')} className="sortable">
                Биржа {getSortIcon('exchange')}
              </th>
              <th onClick={() => handleSort('timeframes')} className="sortable">
                Таймфреймы {getSortIcon('timeframes')}
              </th>
              <th onClick={() => handleSort('ttl')} className="sortable">
                TTL {getSortIcon('ttl')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredPairs.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">
                  {searchTerm ? 'Ничего не найдено' : 'Нет данных в кэше'}
                </td>
              </tr>
            ) : (
              filteredPairs.map((item, index) => (
                <tr key={`${item.pair}-${item.exchange}`} style={{ animationDelay: `${index * 0.02}s` }}>
                  <td>
                    <span className="pair-name">{item.pair}</span>
                  </td>
                  <td>
                    <span className="exchange-badge">{item.exchange}</span>
                  </td>
                  <td>
                    <div className="timeframes-list">
                      {item.timeframes.map(tf => (
                        <span key={tf.timeframe} className="timeframe-badge">
                          {tf.timeframe}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className="ttl-display">
                      {item.timeframes.map(tf => (
                        <span key={tf.timeframe} className={`ttl-value ${getTtlColor(tf.ttl)}`}>
                          {tf.ttl}s
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CacheStatus;
